import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import BackgroundEffects from './components/BackgroundEffects';
import ShootingStar from './components/ShootingStar';
import CustomCursor from './components/CustomCursor';
import FloatingMascot from './components/FloatingMascot';
import PasswordGate from './components/PasswordGate';
import DashboardSidebar from './components/DashboardSidebar';
import DashboardHeader from './components/DashboardHeader';
import ToolFilters from './components/ToolFilters';
import QuickAccess from './components/QuickAccess';
import AppCard from './components/AppCard';
import SafeIcon from './common/SafeIcon';
import { useToolPreferences } from './hooks/useToolPreferences';
import { appsData, categories } from './data/appsData';
import './wide-screen.css';

const taskMatches = {
  Projects: ['Projects', 'Planning', 'Teams'],
  Booking: ['Booking', 'Workspace', 'Facilities', 'Scheduling'],
  Files: ['Files', 'Server', 'Storage', 'Footage'],
  People: ['People', 'Culture', 'Hiring', 'Policies'],
};

const getInitialAccess = () =>
  sessionStorage.getItem('vicinity-access') === 'granted';

function Dashboard({ onLock }) {
  const [category, setCategory] = useState('All');
  const [task, setTask] = useState('All tasks');
  const [query, setQuery] = useState('');
  const preferences = useToolPreferences();

  const filteredApps = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return appsData.filter((app) => {
      const searchable = [
        app.title,
        app.label,
        app.description,
        app.domain,
        app.category,
        app.owner,
        ...app.tags,
        ...app.searchTerms,
      ].join(' ').toLowerCase();

      const categoryMatch = category === 'All' || app.category === category;
      const taskMatch = task === 'All tasks'
        || app.tags.some((tag) => taskMatches[task]?.includes(tag));

      return categoryMatch && taskMatch && searchable.includes(normalizedQuery);
    });
  }, [category, task, query]);

  const currentApps = filteredApps.filter((app) => !app.legacy);
  const olderApps = filteredApps.filter((app) => app.legacy);

  const clearFilters = () => {
    setQuery('');
    setCategory('All');
    setTask('All tasks');
  };

  return (
    <div className="dashboard-shell min-h-screen">
      <BackgroundEffects />
      <ShootingStar />
      <DashboardSidebar
        categories={categories}
        activeCategory={category}
        apps={appsData}
        onCategoryChange={setCategory}
        onLock={onLock}
      />

      <main className="dashboard-main relative z-10">
        <DashboardHeader total={appsData.length} onLock={onLock} />

        <QuickAccess
          favourites={preferences.favourites}
          recent={preferences.recent}
          onOpen={preferences.recordOpen}
        />

        <div className="directory-heading">
          <div>
            <p className="section-kicker">Complete directory</p>
            <h2>Find a tool</h2>
          </div>
        </div>

        <ToolFilters
          searchQuery={query}
          onSearchChange={setQuery}
          taskFilter={task}
          onTaskChange={setTask}
          resultCount={filteredApps.length}
          compact={preferences.compact}
          onToggleCompact={preferences.toggleCompact}
          categories={categories}
          activeCategory={category}
          onCategoryChange={setCategory}
        />

        {filteredApps.length ? (
          <>
            <motion.div
              layout
              className={preferences.compact ? 'tools-grid compact' : 'tools-grid'}
            >
              <AnimatePresence mode="popLayout">
                {currentApps.map((app) => (
                  <AppCard
                    key={app.id}
                    app={app}
                    compact={preferences.compact}
                    isFavourite={preferences.favouriteIds.includes(app.id)}
                    onToggleFavourite={preferences.toggleFavourite}
                    onOpen={preferences.recordOpen}
                  />
                ))}
              </AnimatePresence>
            </motion.div>

            {olderApps.length > 0 && (
              <section className="older-resources">
                <div className="older-heading">
                  <SafeIcon name="Archive" />
                  <div>
                    <h2>Older resources</h2>
                    <p>Historical tools retained for reference. Use current services where possible.</p>
                  </div>
                </div>

                <div className="tools-grid compact">
                  {olderApps.map((app) => (
                    <AppCard
                      key={app.id}
                      app={app}
                      compact
                      isFavourite={preferences.favouriteIds.includes(app.id)}
                      onToggleFavourite={preferences.toggleFavourite}
                      onOpen={preferences.recordOpen}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        ) : (
          <div className="empty-results">
            <SafeIcon name="Search" />
            <h2>No matching tools found</h2>
            <p>Try a different task, tool name, or category.</p>
            <button type="button" onClick={clearFilters}>
              Clear all filters
            </button>
          </div>
        )}

        <footer className="dashboard-footer">
          <p>© 2026 Vicinity · Developed by Vicinity IT Team</p>
          <div>
            <a href="mailto:it@vicinity.studio?subject=Tools%20portal%20access">
              Request access
            </a>
            <a href="mailto:it@vicinity.studio?subject=Tools%20portal%20problem">
              Report a problem
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default function App() {
  const [unlocked, setUnlocked] = useState(getInitialAccess);

  const unlock = () => {
    sessionStorage.setItem('vicinity-access', 'granted');
    setUnlocked(true);
  };

  const lock = () => {
    sessionStorage.removeItem('vicinity-access');
    setUnlocked(false);
  };

  return (
    <>
      <CustomCursor />
      <FloatingMascot />
      <AnimatePresence mode="wait">
        {unlocked ? (
          <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Dashboard onLock={lock} />
          </motion.div>
        ) : (
          <motion.div key="gate" exit={{ opacity: 0, scale: 0.99 }}>
            <PasswordGate onUnlock={unlock} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}